<template>
<div class="common-flexbox flex-direction-column login-container">
    <VeeForm class="form-container" @submit="login">
        <div class="common-flexbox flex-content-start-align">
            <div class="heading declaration-heading">{{ getEmailHeader }}</div>
            <VeeField name="companyEmailAddress" :rules="rules" mode="aggressive" validateOnBlur v-slot="{ field, errors, handleBlur }">
                <InputBox :field="field" id="companyEmailAddressInput" class="email-input" name="email" type="text" :modelValue="emailAddress" :label="$t('login.email_placeholder')" @update="handleEmailChange" :regFlowLogin="true" @blur="handleFieldBlur($event, handleBlur)" />
                <div v-if="errors.length && hasBlurred" class="align-left error-message">{{ getErrorMessage($t('form.company_email_address'), errors[0]) }}</div>
                <div class="common-flexbox flex-content-left-align login-button-container">
                    <button type="button" id="emailEntryButton" class="common-button mt-60"
                            :disabled="!isEmailValid"
                            @click="login">
                        {{ $t('login.continue') }}
                    </button>
                </div>
            </VeeField>
        </div>
    </VeeForm>
    <PrivacyPolicyModal :show="showPrivacyPolicyModal" :onClose="closePrivacyPolicyModal" :onContinue="agreePrivacyPolicy"></PrivacyPolicyModal>
</div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    useIsFormDirty,
    useIsFormValid
} from 'vee-validate';

import {
    InputBox
} from '@/components/ui/Form';
import {
    getAllUrlQueryParam
} from '@/utils/helper';
import Constants from '@/utils/constants';
import PrivacyPolicyModal from '@/components/common/PrivacyPolicyModal';

// Composables
import { 
    useLogin, 
    useConferenceCall, 
    useRouting 
} from '@/composables';

// Setup composables
const { t } = useI18n();
const { changeUrl } = useRouting();

// Login composable
const {
    isValidEmail,
    redirectTo,
    showPrivacyPolicyModal,
    loggedInUser,
    userEmailAddress,
    canBypassReg,
    loginUser,
    setPrivacyPolicyModalFlag,
    updatePrivacyPolicyErrorMessage,
    checkUserVerifiedHisEmail,
    checkUserEmailVerifiedEmail,
    getLoggedInUserDetailsWithoutRedirectOnError,
    savePrivacyPolicyConsent
} = useLogin();

// Conference call composable
const {
    isSGM,
    conferenceCallDetails,
    conferenceCallStatus,
    isReplayEnable,
    isEndedStatus,
    isExpiredStatus
} = useConferenceCall();

// Reactive data
const rules = {
    required: true,
    regex: Constants.EmailAddressRegex,
};
const emailAddress = ref('');
const hasBlurred = ref(false);
const isSubmitting = ref(false);

// Computed properties
const invalidDomainMessage = computed(() => {
    return isValidEmail.value ? '' : 'invalid_email_domain';
});

const getEmailHeader = computed(() => {
    if (isReplayEnable.value && (isEndedStatus.value || isExpiredStatus.value)) {
        return t('login.view_replay');
    } else {
        return t('login.email_label');
    }
});

const isEmailValid = computed(() => {
    return emailAddress.value && emailAddress.value.match(Constants.EmailAddressRegex);
});

// Watch for redirect changes
watch(redirectTo, (newUrl) => {
    changeUrl(newUrl);
});

// Methods
const handleEmailChange = (value) => {
    emailAddress.value = value;
    // If the user starts typing, clear any validation errors
    if (value && isEmailValid.value) {
        hasBlurred.value = false;
    }
};

const handleFieldBlur = (event, handleBlur) => {
    // Only set hasBlurred to true if the field value is invalid
    // This way, if the email is valid (like one from sessionStorage), we won't show errors
    if (!isEmailValid.value) {
        hasBlurred.value = true;
    }

    // Call vee-validate's handleBlur if provided
    if (handleBlur && typeof handleBlur === 'function') {
        handleBlur(event);
    }
};

const login = async (event) => {
    // Prevent default form submission if event is provided
    if (event && event.preventDefault) {
        event.preventDefault();
    }

    // Don't proceed if email is empty or invalid
    if (!isEmailValid.value) {
        isSubmitting.value = false;
        hasBlurred.value = true; // Show validation errors
        return;
    }

    // Prevent double submissions
    if (isSubmitting.value) {
        return;
    }
    isSubmitting.value = true;

    // Ensure we have the email in lowercase
    const email = emailAddress.value.toLowerCase();

    try {
        // Call the login action
        await loginUser(email);
    } finally {
        // Reset submission flag after a short delay
        setTimeout(() => {
            isSubmitting.value = false;
        }, 1000);
    }
};

const closePrivacyPolicyModal = () => {
    setPrivacyPolicyModalFlag(false);
    updatePrivacyPolicyErrorMessage(Constants.ErrorMessages.PrivacyPolicyNotAccepted);
};

const agreePrivacyPolicy = async () => {
    setPrivacyPolicyModalFlag(false);
    let consentData = {
        UserName: emailAddress.value.toLowerCase(),
        IsConsentingToPrivacyPolicy: true,
    };
    await savePrivacyPolicyConsent(consentData);
    if (!canBypassReg.value) await checkUserVerifiedHisEmail();
    checkUserEmailVerifiedEmail();
};

const getErrorMessage = (field, error) => {
    return t(error, {
        field: field
    });
};

// Lifecycle hooks
const initializeComponent = async () => {
    emailAddress.value = getAllUrlQueryParam('email') || '';
    if (!emailAddress.value && userEmailAddress.value) {
        // For pre-filled emails, we want to keep hasBlurred as false to prevent validation errors from showing
        hasBlurred.value = false;
    }

    if (emailAddress.value) {
        await login();
    } else if (isSGM.value) {
        await getLoggedInUserDetailsWithoutRedirectOnError();
        if (loggedInUser.value.EmailAddress) {
            emailAddress.value = loggedInUser.value.EmailAddress;
            await login();
        }
    }
};

onMounted(async () => {
    await initializeComponent();
    emailAddress.value = emailAddress.value || userEmailAddress.value;
    sessionStorage.removeItem(Constants.SessionStorageKeys.IsPublicParticipant);
    sessionStorage.removeItem(Constants.SessionStorageKeys.IsPresenter);
});


</script>

<style lang="stylus" scoped>
@import '~@/shared/styles/common.scss';
@import '~@/shared/styles/formControl.scss';

.form-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    flex: 1;
    min-height: 200px;
}

.gutter-vertical-20 {
    margin-top: 20px;
}

.error-label {
    display: flex;
}

.mt-60 {
    margin-top: 60px;
}

.login-container {
    margin: 0 40% 0 0;

    #emailEntryButton {
        width: 258px;
        background: var(--primary-color);

        &:disabled {
            opacity: 0.5;
        }
    }

    @media screen and (max-width: 1024px) {
        width: 82%;
    }

    @media screen and (max-width: 768px) {
        width: 100%;
        margin: auto;
    }
}

.heading {
    font-family: 'Lato', sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;
    letter-spacing: 0.0994px;
    color: #3a4145;
}

.declaration-heading {
    margin-bottom: 16px;
    text-align: left;
}

.align-left {
    text-align: left;
}
</style>